import React, { useCallback } from 'react';
import classnames from 'classnames';
import { Col, Grid, Section } from '@fiverr-private/fit';
import { useBreakpoints } from '@fiverr-private/business_blocks';
import { Expertise, SafeWrapper } from '@fiverr-private/seller_pages_toolbox';
import { AboutMe } from '../AboutMe';
import { SOURCES } from '../../../monitoring/monitoring-constants';
import { useMonitoring } from '../../../monitoring/index';
import Modal from '../../common/Modal';
import { LazyNotableClients } from '../../common/NotableClients';
import { ProOverview, SPACING_MODES } from '../../common/ProOverview';
import { ButtonsGroup, ContactButton, DotsMenuButton, SaveToListButton } from '../../common/ActionButtons';
import FreelancerProfile from '../FreelancerProfile';
import { useFreelancerPageContext } from '../../FreelancerPageContext';
import { scrollToTab, TabName } from '../StickyHeader/tab-names';
import { Educations } from './Educations';
import { Certifications } from './Certifications';
import { FiverrLearn } from './FiverrLearn';
import { ModalHeader } from './ModalHeader';

import classes from './index.module.scss';

/** @type {import('./index').LearnMore} */
const LearnMoreModal = ({ children }) => {
    const { username, isLoggedIn, seller, sellerId, setIsShowLearnModal } = useFreelancerPageContext();
    const { fireEvents, Providers } = useMonitoring();
    const { isMD } = useBreakpoints();

    const educations = seller.activeEducations || [];
    const certifications = seller.certifications || [];
    const courses = seller?.courses || [];
    const showProOverview = seller?.isPro;
    const onModalOpenBI = useCallback(() => {
        fireEvents({
            [Providers.BigQuery]: {
                type: 'about_business_seller_modal_is_shown',
            },
            [Providers.Mixpanel]: {
                type: 'view about modal',
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onModalCloseBI = useCallback(() => {
        fireEvents({
            [Providers.Mixpanel]: {
                type: 'close about modal',
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onOpen = useCallback(
        (open) => () => {
            setIsShowLearnModal(true);
            onModalOpenBI();
            return open();
        },
        [setIsShowLearnModal, onModalOpenBI]
    );

    const onClose = useCallback(() => {
        onModalCloseBI();
        setIsShowLearnModal(false);
    }, [setIsShowLearnModal, onModalCloseBI]);

    const onRatingsClick = useCallback(
        (close) => () => {
            scrollToTab(TabName.Reviews);
            close();
        },
        []
    );

    return (
        <Modal
            overlayClassName={classnames(classes.modalOverlay, 'learn-more-modal')}
            trigger={({ open }) => React.cloneElement(children, { onClick: onOpen(open) })}
            onClose={onClose}
        >
            {({ close }) => (
                <Section>
                    <div className={classnames(classes.headerWrapper, 'flex', 'flex-between', 'flex-items-start')}>
                        <ModalHeader
                            className={classnames(classes.header, 'flex')}
                            onRatingsClick={onRatingsClick(close)}
                        />
                        <div className={classes.infoButtons}>
                            <ButtonsGroup className="flex-end">
                                <ContactButton
                                    className={classes.buttonContact}
                                    username={username}
                                    source={SOURCES.modal}
                                    renderDropdown
                                />
                                <SaveToListButton sellerId={sellerId} />
                                <DotsMenuButton user={seller.user} source={SOURCES.modal} isLoggedIn={isLoggedIn} />
                            </ButtonsGroup>
                        </div>
                    </div>
                    <Grid theme={Grid.THEMES.TWELVE}>
                        <Col md={7} sm={12}>
                            {showProOverview && (
                                <ProOverview
                                    proSubcategories={seller?.proSubCategories}
                                    spacingMode={SPACING_MODES.LARGE}
                                />
                            )}
                            <AboutMe
                                description={seller?.description}
                                username={username}
                                withClamp={false}
                                className="m-b-24"
                            />
                            <Expertise
                                className="m-b-24"
                                filterType={showProOverview ? 'only_skills' : 'none'}
                                withClamp={false}
                                shouldLinkSkills={false}
                            />
                            <LazyNotableClients
                                className={classes.notableClients}
                                user={seller.user}
                                source={SOURCES.modal}
                                verticalOnSm
                                wrapTitle
                            />
                            {!!courses.length && <FiverrLearn courses={courses} />}
                            {!!educations.length && <Educations educations={educations} />}
                            {!!certifications.length && <Certifications certifications={certifications} />}
                        </Col>
                        <Col md={4} mdOffset={1} sm={12} xsOffset={0}>
                            <FreelancerProfile seller={seller} />
                        </Col>
                    </Grid>
                </Section>
            )}
            {!isMD && <div className={classes.mobileContact} />}
        </Modal>
    );
};

export default SafeWrapper(LearnMoreModal, 'LearnMoreModal');
